import { FC, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import _ from "lodash";
import { Empty, MenuProps, Modal, Spin } from "../../design-system";
import {
    deleteVendor,
    getVendors,
    manageVendorIndustry,
    updateVendor,
} from "../../common/api/VendorClient";
import { DeleteVendorResponse, Vendor, VendorIndustry, VendorOrderForm } from "../../types/VendorTypes";
import { Supplier } from "../../types/SupplierTypes";
import { OrderForm, OrderFormProcessingStatus } from "../../types/OrderFormTypes";
import { isSuperAdmin } from "../../types/UserGroup";
import { getSupplierByName } from "../../common/api/SupplierClient";
import { handleError } from "../../common/ErrorHandling";
import { useUserState } from "../../common/UserContext";
import { VendorUpdateModal } from "../vendor/components/VendorUpdateModal";
import { VendorDeleteModal } from "../vendor/components/VendorDeleteModal";
import { VendorCard } from "./components/VendorCard";
import { OrderFormStatus, OrderFormUpdated, OrderFormWithStatus, VendorDetailContent } from "./components/VendorDetailContent";
import MOCK_API_RESPONSE from "../../assets/aws-order-forms.after-renewal.json";
import styles from "./VendorDetails.module.less";
import { PagerObject } from "../../types/DataFetchingTypes";

interface VendorOrderFormsUpdated extends Omit<VendorOrderForm, 'metrics' | 'orderForms'> {
    orderForms?: OrderFormUpdated[];
}

const orderFormStatus = (now: number, orderForm: OrderFormUpdated): OrderFormStatus => {
    if (orderForm.projectId !== null && orderForm.processingStatus === OrderFormProcessingStatus.PROCESSING) {
        return OrderFormStatus.UNDER_NEGOTIATION;
    } else if (orderForm.orderEndDate && Date.parse(orderForm.orderEndDate as unknown as string) < now) {
        return OrderFormStatus.EXPIRED;
    } else if (!orderForm.orderStartDate || Date.parse(orderForm.orderStartDate as unknown as string) > now) {
        return OrderFormStatus.FUTURE;
    } else {
        return OrderFormStatus.ACTIVE;
    }
};

const getNow = () => {
    return Date.now();
}

export const VendorDetails: FC = () => {
    const navigate = useNavigate();
    const { me } = useUserState();
    const params = useParams();
    const [vendorKey] = useState(params.vendorKey);
    const [vendorData, setVendorData] = useState<Vendor | null>(null);
    const [supplier, setSupplier] = useState<Supplier>();
    const [orderForms, setOrderForms] = useState<OrderFormWithStatus[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [showUpdate, setShowUpdate] = useState(false);
    const [relatedData, setRelatedData] = useState<DeleteVendorResponse>({});
    const [showDelete, setShowDelete] = useState(false);
    const [includePendingOrderForms, setIncludePendingOrderForms] = useState(false);

    const n: number = getNow();

    const isUserSuperAdmin = isSuperAdmin(me.roles || []);

    // Count active contracts
    const activeContractCount = orderForms.filter((of) => of.orderFormStatus === OrderFormStatus.ACTIVE).length;

    const loadSupplier = async (name: string) => {
        try {
            const res = await getSupplierByName(name);
            setSupplier(res);
        } catch (error) {
            // Supplier may not exist, this is okay
        }
    };

    const loadOrderForms = async () => {
        if (!vendorKey) return;

        try {
            const response = MOCK_API_RESPONSE as PagerObject<VendorOrderFormsUpdated[]>;

            // Find the specific vendor in the response
            const vendorOrderForm = response.data.find((v) => true /*v.vendorKey === vendorKey */); // HACK!

            if (vendorOrderForm) {
                // Update supplier if available
                if (vendorOrderForm.supplier) {
                    setSupplier(vendorOrderForm.supplier);
                }

                // optionally remove order form with status PROCESSING
                const origOrderForms = (vendorOrderForm.orderForms || []).filter((of) => includePendingOrderForms || of.processingStatus !== OrderFormProcessingStatus.PROCESSING);

                // build map from projectId to "root" projectId
                // NOTE: Assumes parentProjectId < projectId
                const projectIdToRootProjectId = new Map<number,number>();
                const parentProjectIds = new Set<number>();
                _.sortBy(origOrderForms, (of) => of.projectId).forEach((of) => {
                    if (of.projectId) {
                        const rootProjectId = of.parentProjectId && projectIdToRootProjectId.get(of.parentProjectId);
                        if (rootProjectId) {
                            projectIdToRootProjectId.set(of.projectId, rootProjectId);
                        } else {
                            projectIdToRootProjectId.set(of.projectId, of.projectId);
                        }

                        if (of.parentProjectId) {
                            parentProjectIds.add(of.parentProjectId);
                        }
                    }
                })

                // Enhance order forms with orderFormStatus
                const orderForms: OrderFormWithStatus[] = origOrderForms.map((of) => ({
                    ...of,
                    orderFormStatus: orderFormStatus(n, of),
                    projectGroupId: of.projectId ? projectIdToRootProjectId.get(of.projectId)! : -(of.id), // TODO: HACK
                    hasChildProject: !!of.projectId && parentProjectIds.has(of.projectId),
                }));

                setOrderForms(orderForms);
            } else {
                setOrderForms([]);
            }
        } catch (error) {
            handleError(error);
            setOrderForms([]);
        }
    };

    const loadVendor = async () => {
        setIsLoading(true);
        try {
            const vendors = await getVendors([vendorKey!]);
            if (vendors && vendors.length > 0) {
                setVendorData(vendors[0]);
            } else {
                setVendorData(null);
            }
        } catch (error) {
            handleError(error);
            setVendorData(null);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (vendorKey) {
            loadVendor();
            loadOrderForms();
        }
    }, [vendorKey, includePendingOrderForms]);

    useEffect(() => {
        if (vendorData?.name) {
            loadSupplier(vendorData.name);
        }
    }, [vendorData]);

    const handleDelete = async () => {
        if (!vendorData) return;

        try {
            const res = await deleteVendor(vendorData);
            if (res.orderForms || res.negotiations) {
                setRelatedData(res);
                setShowDelete(true);
            } else {
                navigate("/vendor");
            }
        } catch (error) {
            handleError(error);
        }
    };

    const handleUpdate = async (updatedVendorData: Vendor, industry?: VendorIndustry) => {
        try {
            await updateVendor(updatedVendorData);
            if (industry?.vendorName && industry?.sectorId && industry.industryId) {
                await manageVendorIndustry(industry);
            }
            await loadVendor();
        } catch (error) {
            handleError(error);
        } finally {
            setShowUpdate(false);
        }
    };

    if (isLoading) {
        return (
            <div className={styles.container} style={{ textAlign: "center", paddingTop: 100 }}>
                <Spin size="large" />
            </div>
        );
    }

    if (!vendorData) {
        return (
            <div className={styles.container}>
                <Empty description="Vendor not found" />
            </div>
        );
    }

    const confirmDelete = () => {
        Modal.confirm({
            title: "Delete vendor",
            content: `Are you sure you want to delete "${vendorData.name}"?`,
            okText: "Delete",
            okButtonProps: { danger: true },
            onOk: handleDelete,
        });
    };

    // Admin actions menu
    const adminActionsMenuItems: MenuProps["items"] = [];
    if (isUserSuperAdmin) {
        adminActionsMenuItems.push({
            key: "edit",
            label: "Edit",
            onClick: () => setShowUpdate(true),
        });
        adminActionsMenuItems.push({
            key: "delete",
            label: "Delete",
            danger: true,
            onClick: confirmDelete,
        });
    }

    const renderContent = () => {
        return (
            <div className={styles.contentContainer}>
                <VendorDetailContent vendor={vendorData} orderForms={orderForms} afterCreateChildProject={async () => setIncludePendingOrderForms(true)}/>
            </div>
        );
    };

    return (
        <div className={styles.container}>
            <div className={styles.stickyHeader}>
                <VendorCard
                    vendor={{
                        ...vendorData,
                        metrics: {
                            ...(vendorData.metrics || { orders: 0, renewalOrders: 0, value: 0 }),
                            orders: activeContractCount,
                        },
                    }}
                    supplier={supplier}
                    noMarginBottom
                    adminActionsMenuItems={adminActionsMenuItems}
                />
            </div>

            {renderContent()}

            <VendorUpdateModal
                open={showUpdate}
                vendor={vendorData}
                onCancel={() => setShowUpdate(false)}
                onOk={handleUpdate}
            />
            <VendorDeleteModal
                open={showDelete}
                orderForms={relatedData.orderForms}
                negotiations={relatedData.negotiations}
                onCancel={() => setShowDelete(false)}
                onOk={() => setShowDelete(false)}
            />
        </div>
    );
};
